import React, { createContext, useCallback, useMemo, useState } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  exportReportToExcel,
  ExcelColumn
} from 'utils/hooks/exportReportToExcel';

export interface ReportExportContextValue {
  registerReport: (
    data: any[],
    columns: ExcelColumn[],
    fileName: string,
    sheetName?: string
  ) => void;

  clearReport: () => void;

  exportReport: () => void;

  hasData: boolean;
}

export const ReportExportContext = createContext<
  ReportExportContextValue | undefined
>(undefined);

interface ReportExportProviderProps {
  children: React.ReactNode;
}

export const ReportExportProvider: React.FC<ReportExportProviderProps> = ({
  children
}) => {
  const { t } = useTranslation();

  const [reportData, setReportData] = useState<{
    data: any[];
    columns: ExcelColumn[];
    fileName: string;
    sheetName: string;
  } | null>(null);

  const registerReport = useCallback(
    (
      data: any[],
      columns: ExcelColumn[],
      fileName: string,
      sheetName = 'Report'
    ) => {
      setReportData({
        data,
        columns,
        fileName,
        sheetName
      });
    },
    []
  );

  const clearReport = useCallback(() => {
    setReportData(null);
  }, []);

  const exportReport = useCallback(() => {
    if (!reportData || reportData.data.length === 0) {
      message.warning(t('NO_DATA_TO_EXPORT', 'No data available to export'));

      return;
    }

    try {
      const exported = exportReportToExcel(
        reportData.data,
        reportData.columns,
        reportData.fileName,
        reportData.sheetName
      );

      if (exported) {
        message.success(
          t('REPORT_EXPORTED_SUCCESSFULLY', 'Report exported successfully')
        );
      }
    } catch (error) {
      console.error('Failed to export report:', error);

      message.error(t('REPORT_EXPORT_FAILED', 'Failed to export report'));
    }
  }, [reportData, t]);

  const value = useMemo<ReportExportContextValue>(
    () => ({
      registerReport,
      clearReport,
      exportReport,
      hasData: Boolean(reportData && reportData.data.length > 0)
    }),
    [registerReport, clearReport, exportReport, reportData]
  );

  return (
    <ReportExportContext.Provider value={value}>
      {children}
    </ReportExportContext.Provider>
  );
};

export default ReportExportContext;
